import React, { useState } from 'react';
import { ShieldCheck, PenTool } from 'lucide-react';

/**
 * 📁 PROJECT ARCHIVE: THE CLIENT PORTAL
 * A secure repository for legal authorization and Phase 02 architectural retainers.
 */
const ProjectArchive = ({ sessionId, backendUrl }) => {
    const [hasSigned, setHasSigned] = useState(false);
    const [isSigning, setIsSigning] = useState(false);

    const handleSignAgreement = async () => {
        setIsSigning(true);
        // Simulate digital signature timestamping for the forensic record
        setTimeout(() => {
            setHasSigned(true);
            setIsSigning(false);
        }, 1500);
    };

    return (
        <div className="min-h-screen bg-stone-50 p-8 md:p-24 font-sans">
            <div className="max-w-4xl mx-auto">
                <header className="mb-12 border-b border-stone-200 pb-8">
                    <h1 className="text-[10px] uppercase tracking-[0.4em] text-stone-500 font-bold mb-2">Michael's Technical Design</h1>
                    <p className="text-4xl font-serif text-stone-900 italic">Project Archive & Authorization</p>
                </header>

                {!hasSigned ? (
                    <section className="bg-white border border-stone-200 p-12 shadow-sm relative overflow-hidden">
                        <div className="relative z-10">
                            <div className="flex items-center gap-3 text-amber-600 mb-6">
                                <ShieldCheck size={24} />
                                <span className="text-[10px] uppercase tracking-widest font-bold">Action Required: Strategic Authorization</span>
                            </div>
                            <h2 className="text-2xl font-serif mb-4">Consultation Agreement & SOW</h2>
                            <p className="text-stone-600 mb-8 leading-relaxed">
                                To proceed with the **System Architecture & Technical Specifications**, please review and authorize the consultation agreement. This document outlines the Principal's advisory role and the protected delivery of technical intellectual property.
                            </p>
                            
                            {/* LEGAL SCROLL BOX */}
                            <div className="bg-stone-50 border border-stone-100 p-8 mb-8 h-64 overflow-y-scroll text-sm text-stone-500 font-serif italic leading-relaxed">
                                <p className="mb-4">1. SCOPE OF ADVISORY: Michael’s Technical Design (The Consultant) shall provide specialized architectural oversight and design synthesis for the system infrastructure.</p>
                                <p className="mb-4">2. INTELLECTUAL PROPERTY: All designs, red-lines, specifications, and strategic directives remain the sole property of the Consultant until final Phase 03 release.</p>
                                <p>3. LIMITATION OF LIABILITY: The Consultant acts in a strategic advisory capacity only. Physical implementation is the sole responsibility of the client's chosen licensed contractors.</p>
                            </div>

                            <button 
                                onClick={handleSignAgreement}
                                disabled={isSigning}
                                className="w-full bg-stone-900 text-white py-5 font-bold uppercase text-[11px] tracking-[0.3em] flex items-center justify-center gap-3 hover:bg-stone-800 transition-all disabled:opacity-50"
                            >
                                {isSigning ? "Recording Signature..." : "Digitally Authorize & Unlock Retainer"} <PenTool size={14}/>
                            </button>
                        </div>
                    </section>
                ) : (
                    <section className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <div className="bg-emerald-50 border border-emerald-100 p-6 flex items-center gap-4 text-emerald-800 mb-8">
                            <ShieldCheck size={20} />
                            <span className="text-[10px] uppercase tracking-widest font-bold">Agreement Authorized: {new Date().toLocaleDateString()}</span>
                        </div>
                        
                        {/* THE STRIPE PAYMENT GATEWAY FOR CONSULTATION PHASE 02 */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-white border border-stone-200 p-8 shadow-sm">
                                <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">Advisory Phase 02</p>
                                <h3 className="text-xl font-serif mb-4">Design & Specification Retainer</h3>
                                <p className="text-stone-500 text-sm mb-8 font-serif leading-relaxed">
                                    Commission full architectural blueprints, material specifications, and a forensic risk assessment of your current infrastructure.
                                </p>
                                <button className="w-full bg-amber-600 text-white px-8 py-4 font-bold uppercase text-[10px] tracking-widest hover:bg-amber-700 transition-colors">
                                    Secure Design Phase — $2,500
                                </button>
                            </div>

                            <div className="bg-stone-900 p-8 flex flex-col justify-center items-center text-center">
                                <ShieldCheck className="text-amber-500 mb-4" size={32} />
                                <h4 className="text-white font-serif text-lg mb-2">Vault Access Active</h4>
                                <p className="text-stone-400 text-[10px] uppercase tracking-widest">Awaiting Design Intake</p>
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default ProjectArchive;